/**
 * One-time charges + rebates — Phase 3 Charges.
 *
 * Lists ad-hoc charges (festival fund / fines / damage) and rebates issued
 * against units. Admin can create / edit-while-pending / cancel / apply to
 * an existing bill.
 *
 * Lifecycle:
 *   pending   — visible in pickers; editable
 *   applied   — bundled into a bill (line item created); read-only
 *   cancelled — admin aborted before applying
 *   paid      — standalone settle (future)
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, CheckCircle2, Wallet, Gift, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';

type Status = 'pending' | 'applied' | 'cancelled' | 'paid';

interface OneTimeCharge {
  id: string;
  unit_id: string;
  charge_type_id: string | null;
  label: string;
  description: string | null;
  amount_inr: number;
  due_date: string | null;
  is_rebate: boolean;
  applied_to_bill_id: string | null;
  status: Status;
  notes: string | null;
  created_at: string;
  unit?: { id: string; number: string };
  charge_type?: { id: string; code: string; label: string } | null;
}

interface ListResponse {
  items: OneTimeCharge[];
  next_cursor?: string | null;
}

interface UnitOption {
  id: string;
  number: string;
}

interface ChargeTypeOption {
  id: string;
  code: string;
  label: string;
}

const PAGE_SIZE = 50;

const STATUS_VARIANT: Record<Status, 'info' | 'ok' | 'alert' | 'muted'> = {
  pending: 'info',
  applied: 'ok',
  cancelled: 'muted',
  paid: 'ok'
};

const CreateSchema = z
  .object({
    unit_id: z.string().uuid('Select a flat'),
    charge_type_id: z.string().uuid().optional().or(z.literal('').transform(() => undefined)),
    label: z.string().trim().min(2, 'Label required').max(160),
    description: z.string().trim().max(500).optional().or(z.literal('')),
    amount_inr: z.coerce.number().int().min(-1_000_000).max(1_000_000),
    due_date: z.string().optional().or(z.literal('')),
    is_rebate: z.boolean(),
    notes: z.string().trim().max(500).optional().or(z.literal(''))
  })
  .refine((v) => v.amount_inr !== 0, { message: 'Amount cannot be zero' })
  .refine((v) => (v.is_rebate ? v.amount_inr < 0 : v.amount_inr > 0), {
    message: 'Rebate amount must be negative; regular charge must be positive'
  });
type CreateForm = z.infer<typeof CreateSchema>;

const DEFAULT_FORM: CreateForm = {
  unit_id: '',
  charge_type_id: undefined,
  label: '',
  description: '',
  amount_inr: 0,
  due_date: '',
  is_rebate: false,
  notes: ''
};

function inr(n: number): string {
  const abs = Math.abs(n);
  return `${n < 0 ? '−' : ''}₹${abs.toLocaleString('en-IN')}`;
}

export function OneTimeChargesPage(): JSX.Element {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [isRebateFilter, setIsRebateFilter] = useState<'all' | 'rebate' | 'charge'>('all');
  const [open, setOpen] = useState(false);
  const [applyDialogFor, setApplyDialogFor] = useState<OneTimeCharge | null>(null);
  const [applyBillId, setApplyBillId] = useState('');

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULT_FORM,
    mode: 'onChange'
  });

  // Cursor pagination resets whenever filters change.
  const filtersHash = `${statusFilter}|${isRebateFilter}`;
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['one-time-charges', filtersHash, cursor],
    queryFn: () =>
      get<ListResponse>('/one-time-charges', {
        params: {
          limit: PAGE_SIZE,
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(isRebateFilter === 'rebate' ? { is_rebate: true } : {}),
          ...(isRebateFilter === 'charge' ? { is_rebate: false } : {}),
          ...(cursor ? { cursor } : {})
        }
      }),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<OneTimeCharge>(listQuery.data, cursor, filtersHash);
  const items = paginator.items;

  const unitsQuery = useQuery({
    queryKey: ['units-flat'],
    queryFn: () => get<{ items: UnitOption[] }>('/society/units', { params: { limit: 500 } }),
    staleTime: 60_000
  });
  const units = unitsQuery.data?.items ?? [];

  const chargeTypesQuery = useQuery({
    queryKey: ['charge-types-flat'],
    queryFn: () => get<{ items: ChargeTypeOption[] }>('/charge-types', { params: { limit: 200 } })
  });
  const chargeTypes = chargeTypesQuery.data?.items ?? [];

  const pendingTotal = useMemo(
    () =>
      items
        .filter((i) => i.status === 'pending' && !i.is_rebate)
        .reduce((s, i) => s + i.amount_inr, 0),
    [items]
  );
  const pendingRebates = useMemo(
    () =>
      items
        .filter((i) => i.status === 'pending' && i.is_rebate)
        .reduce((s, i) => s + Math.abs(i.amount_inr), 0),
    [items]
  );

  const create = useMutation({
    mutationFn: async (v: CreateForm) => {
      const payload = {
        unit_id: v.unit_id,
        charge_type_id: v.charge_type_id || undefined,
        label: v.label,
        description: v.description?.trim() ? v.description.trim() : undefined,
        amount_inr: v.is_rebate ? -Math.abs(v.amount_inr) : Math.abs(v.amount_inr),
        is_rebate: v.is_rebate,
        due_date: v.due_date ? new Date(v.due_date).toISOString() : undefined,
        notes: v.notes?.trim() ? v.notes.trim() : undefined
      };
      return post<OneTimeCharge>('/one-time-charges', payload);
    },
    onSuccess: () => {
      toast.success('Charge created');
      setOpen(false);
      form.reset(DEFAULT_FORM);
      void qc.invalidateQueries({ queryKey: ['one-time-charges'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const cancel = useMutation({
    mutationFn: (id: string) => post<OneTimeCharge>(`/one-time-charges/${id}/cancel`, {}),
    onSuccess: () => {
      toast.success('Charge cancelled');
      void qc.invalidateQueries({ queryKey: ['one-time-charges'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const applyToBill = useMutation({
    mutationFn: ({ id, bill_id }: { id: string; bill_id: string }) =>
      post<OneTimeCharge>(`/one-time-charges/${id}/apply-to-bill`, { bill_id }),
    onSuccess: () => {
      toast.success('Charge applied to bill');
      setApplyDialogFor(null);
      setApplyBillId('');
      void qc.invalidateQueries({ queryKey: ['one-time-charges'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="One-time charges & rebates"
        description="Ad-hoc charges and credits issued against units. Bundle into bills when generating a cycle."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Issue charge / rebate
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Shown</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{items.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Pending charges</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{inr(pendingTotal)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Pending rebates</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{inr(-pendingRebates)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4 flex items-center gap-vt-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
              <Wallet className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div>
              <div className="text-vt-12 text-vt-text-muted">Phase</div>
              <div className="text-vt-14 font-semibold text-vt-text">3 of 3</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-vt-3 flex flex-wrap items-center gap-vt-2">
          <Label className="text-vt-12 text-vt-text-muted">Status</Label>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as Status | '')}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="applied">Applied</option>
            <option value="cancelled">Cancelled</option>
            <option value="paid">Paid</option>
          </Select>
          <Label className="ml-vt-4 text-vt-12 text-vt-text-muted">Kind</Label>
          <Select value={isRebateFilter} onChange={(e) => setIsRebateFilter(e.target.value as 'all' | 'rebate' | 'charge')}>
            <option value="all">All</option>
            <option value="charge">Charges only</option>
            <option value="rebate">Rebates only</option>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-vt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Gift className="h-8 w-8" strokeWidth={1.5} />}
              title="No one-time charges yet"
              description="Issue a one-shot fee, fine, or rebate from the button above."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Charge type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.unit?.number ?? '—'}</TableCell>
                    <TableCell className="text-vt-13">{row.label}</TableCell>
                    <TableCell className="text-vt-12 text-vt-text-muted">
                      {row.charge_type?.label ?? '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-vt-13">{inr(row.amount_inr)}</TableCell>
                    <TableCell>
                      {row.is_rebate ? (
                        <Badge variant="muted">Rebate</Badge>
                      ) : (
                        <Badge variant="info">Charge</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
                    </TableCell>
                    <TableCell className="text-vt-12 text-vt-text-muted">
                      {new Date(row.created_at).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.status === 'pending' ? (
                        <div className="inline-flex gap-vt-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setApplyDialogFor(row)}
                          >
                            <Receipt className="h-3 w-3" /> Apply to bill
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancel.mutate(row.id)}
                            disabled={cancel.isPending}
                          >
                            <X className="h-3 w-3" /> Cancel
                          </Button>
                        </div>
                      ) : row.status === 'applied' ? (
                        <span className="inline-flex items-center gap-1 text-vt-12 text-vt-text-muted">
                          <CheckCircle2 className="h-3 w-3" /> Bundled
                        </span>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="p-vt-3">
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={listQuery.isFetching}
              onLoadMore={() => paginator.nextCursor && setCursor(paginator.nextCursor)}
              itemsShown={items.length}
              pageSize={PAGE_SIZE}
            />
          </div>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue one-time charge or rebate</DialogTitle>
            <DialogDescription>
              Use a negative amount + tick "This is a rebate" to issue a credit.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit((v) => create.mutate(v))}
            className="flex flex-col gap-vt-3"
            noValidate
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="otc-unit">Unit</Label>
              <Select id="otc-unit" {...form.register('unit_id')}>
                <option value="">Select a flat</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.number}
                  </option>
                ))}
              </Select>
              {form.formState.errors.unit_id ? (
                <p className="text-vt-12 text-vt-alert">{form.formState.errors.unit_id.message}</p>
              ) : null}
            </div>

            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="otc-ct">Charge type (optional)</Label>
              <Select id="otc-ct" {...form.register('charge_type_id')}>
                <option value="">— No catalog link —</option>
                {chargeTypes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="otc-label">Label</Label>
              <Input id="otc-label" maxLength={160} {...form.register('label')} />
              {form.formState.errors.label ? (
                <p className="text-vt-12 text-vt-alert">{form.formState.errors.label.message}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="otc-amount">Amount (₹)</Label>
                <Input
                  id="otc-amount"
                  type="number"
                  min={-1_000_000}
                  max={1_000_000}
                  {...form.register('amount_inr')}
                />
                {form.formState.errors.amount_inr ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.amount_inr.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="otc-due">Due date (optional)</Label>
                <Input id="otc-due" type="date" {...form.register('due_date')} />
              </div>
            </div>

            <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
              <input
                type="checkbox"
                {...form.register('is_rebate')}
                className="h-4 w-4 accent-vt-primary"
              />
              This is a rebate (negative impact on bill)
            </label>

            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="otc-notes">Notes (optional)</Label>
              <Textarea id="otc-notes" rows={2} maxLength={500} {...form.register('notes')} />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={create.isPending}
                loadingText="Saving…"
                disabled={create.isPending || !form.formState.isValid}
              >
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply-to-bill dialog */}
      <Dialog
        open={Boolean(applyDialogFor)}
        onOpenChange={(v) => {
          if (!v) {
            setApplyDialogFor(null);
            setApplyBillId('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply to bill</DialogTitle>
            <DialogDescription>
              Paste the bill ID to bundle this {applyDialogFor?.is_rebate ? 'rebate' : 'charge'} as a
              line item. The bill must belong to the same unit and not be paid/waived.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-2">
            <Label htmlFor="otc-billid">Bill ID</Label>
            <Input
              id="otc-billid"
              value={applyBillId}
              onChange={(e) => setApplyBillId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setApplyDialogFor(null);
                setApplyBillId('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              loading={applyToBill.isPending}
              loadingText="Applying…"
              disabled={!applyBillId || applyToBill.isPending}
              onClick={() => {
                if (applyDialogFor && applyBillId) {
                  applyToBill.mutate({ id: applyDialogFor.id, bill_id: applyBillId });
                }
              }}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
